import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getCachedSession, signOutWithJwt } from "@/lib/auth-client";

export const Route = createFileRoute("/no-access")({
  component: NoAccessPage,
  beforeLoad: async ({ location }) => {
    const { data: session, error } = await getCachedSession();
    if (error || !session) {
      throw redirect({
        to: "/auth/signin",
        search: { redirect: location.href },
      });
    }
    const role = (session.user as { role?: string | null } | undefined)?.role;
    // Platform admins have full access and should never land here — bounce
    // them back to the dashboard so this page isn't a dead-end. Non-admins
    // reach this page for two reasons, both legitimate: zero site claims
    // (redirected from the dashboard route) or hitting an admin-only route
    // they can't access (requireMinRole('admin'), CTE-127). Either way they
    // stay here and see the "not authorised" message.
    if (role === "admin") {
      throw redirect({ to: "/dashboard" });
    }
  },
});

function NoAccessPage() {
  const navigate = useNavigate();

  return (
    <div className="bg-topography flex min-h-svh items-center justify-center p-6 md:p-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>You don't have access to this page</CardTitle>
          <CardDescription>
            Access in iPlant is granted per asset and per role.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6 px-6">
          <p className="text-muted-foreground text-sm leading-relaxed">
            Your account doesn't have the access needed for this page — either
            it isn't linked to any assets yet, or this area is limited to
            administrators. Ask an administrator at your organisation to update
            your access, then sign in again to pick up the change.
          </p>
          <Button
            onClick={async () => {
              await signOutWithJwt();
              navigate({ to: "/auth/signin" });
            }}
            className="w-full"
          >
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
